import { getEnvironment } from '../../../config';

/** Default OpenAI-compatible base URL when none is configured — the V14 LiteLLM gateway. */
export const DEFAULT_BASE_URL = 'https://gateway.v14.tools/v1';

/**
 * Resolves a request URL for an OpenAI-compatible endpoint path (e.g.
 * `/chat/completions`, `/models`) against the configured base URL.
 *
 * - **Development** — route through the webpack dev-server proxy (`/api/ai`),
 *   carrying the real target in the `x-openai-base` header so the proxy can
 *   forward to ANY provider without hitting CORS in the Office webview.
 * - **Production, same-origin** — call `/api/ai/*` on the add-in's own host; a
 *   reverse proxy (Traefik) rewrites it to the gateway's `/v1/*`. Same-origin,
 *   so there is no browser CORS.
 * - **Production, direct** — call `${base}${path}` cross-origin (the gateway must
 *   send CORS headers for the add-in's origin).
 */
export function buildEndpoint(
  baseUrl: string,
  path: string,
): { url: string; extraHeaders: Record<string, string> } {
  const base = (baseUrl || DEFAULT_BASE_URL).replace(/\/+$/, '');
  const p = path.startsWith('/') ? path : `/${path}`;

  if (process.env.NODE_ENV === 'development') {
    return { url: `/api/ai${p}`, extraHeaders: { 'x-openai-base': base } };
  }

  if (getEnvironment().aiProxy === 'same-origin') {
    return { url: `/api/ai${p}`, extraHeaders: {} };
  }

  return { url: `${base}${p}`, extraHeaders: {} };
}

/**
 * Resolves the key-broker URL for a path (e.g. `/key`). The broker is served
 * same-origin at `/api/broker/*` on the add-in's host (Traefik in prod). In dev
 * it's reached through the same webpack proxy, which forwards `/api/broker` to
 * the configured broker host — see `webpack.config.js`. Falls back to
 * same-origin so a mis-set env can't leak the ID token cross-origin.
 */
export function buildBrokerUrl(path: string): string {
  const p = path.startsWith('/') ? path : `/${path}`;
  return `/api/broker${p}`;
}
